// Passkey authentication support
// Bundled as a separate entrypoint for pages that need WebAuthn

import {
  get,
  create,
  parseRequestOptionsFromJSON,
  parseCreationOptionsFromJSON,
} from "@github/webauthn-json/browser-ponyfill";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]').content;
}

function postJSON(url, extraHeaders, body) {
  const options = {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
      ...extraHeaders,
    },
  };
  if (body) options.body = body;
  return fetch(url, options);
}

function checkWebAuthnSupport() {
  if (!window.PublicKeyCredential) {
    alert(
      "Passkeys are not supported in this browser.\n\n" +
        "In local development, passkeys require HTTPS or localhost.\n" +
        "Open chrome://flags/#unsafely-treat-insecure-origin-as-secure\n" +
        "and add: http://auth.lvh.me:3001"
    );
    return false;
  }
  return true;
}

// A WebAuthn ceremony must never run concurrently with itself. Turbo can
// execute this bundle more than once per page, and any duplicate click listener
// would fire a second `new_create_challenge` that overwrites the server
// session's challenge. The authenticator signs the challenge it was handed, so
// the session would then hold a different challenge than the credential ->
// WebAuthn::ChallengeVerificationError. The flag is set synchronously before the
// first await, so a second concurrent invocation is a no-op.
let ceremonyInFlight = false;

async function runCeremony(fn) {
  if (ceremonyInFlight) return;
  ceremonyInFlight = true;
  try {
    await fn();
  } catch (error) {
    if (error && error.name !== "NotAllowedError") {
      console.error("Passkey error:", error);
    }
  } finally {
    ceremonyInFlight = false;
  }
}

// --- Session (Login) ---

// Puts a message where the auth pages already render errors, falling back to the
// form itself so this cannot silently no-op on a page without that container.
function showPasskeyError(message) {
  const host =
    document.querySelector("[data-passkey-error]") ||
    document.getElementById("passkey-form");
  if (!host) return;

  let box = host.querySelector("[data-passkey-error-message]");
  if (!box) {
    box = document.createElement("p");
    box.setAttribute("data-passkey-error-message", "");
    box.className = "mt-2 text-sm text-red-600";
    host.appendChild(box);
  }
  box.textContent = message;
}

async function getChallengeAndSubmitCredential(form) {
  if (!checkWebAuthnSupport()) return;

  const credentialFieldName = form.dataset.credentialFieldName;

  // This binding is SHARED. The user login page uses it, and so does Yadmin's
  // passkey verification gate (admin_users/passkey_verification/show.html.erb
  // renders the same #passkey-form / #passkey-submit-btn ids). So anything sent
  // unconditionally here lands on the admin MFA path too.
  //
  // The identifier therefore travels only when a form actually has one. The user
  // login page sets data-login; the admin gate does not and identifies its user
  // from the session, so its request stays exactly as it was.
  //
  // It goes in the BODY rather than the query string because Rails'
  // filter_parameters could not protect it there (it filters :email, not :login)
  // and the Heroku router logs the full path regardless — so every passkey
  // attempt wrote a user's email into two log streams in cleartext.
  const login = form.dataset.login;
  const challengeResponse = login
    ? await postJSON(
        form.dataset.challengeUrl,
        { "Content-Type": "application/json" },
        JSON.stringify({ login })
      )
    : await postJSON(form.dataset.challengeUrl);

  // A non-2xx here is not a challenge. Without this the error body was fed to
  // parseRequestOptionsFromJSON, which threw, and runCeremony swallowed it —
  // so a rate-limited user saw the button do nothing at all.
  // A non-2xx is not a challenge. Throwing alone is not enough: runCeremony only
  // console.errors, so a rate-limited user still saw the button do nothing —
  // the exact dead-button symptom this work exists to remove. Surface it where
  // the page already shows auth errors, then stop.
  if (!challengeResponse.ok) {
    const message = await challengeResponse
      .json()
      .then((body) => body.error)
      .catch(() => null);
    showPasskeyError(message || "Could not start passkey sign-in. Please try again.");
    return;
  }

  const challengeJSON = await challengeResponse.json();
  const options = parseRequestOptionsFromJSON({ publicKey: challengeJSON });

  try {
    const credential = await get(options);
    form.elements[credentialFieldName].value = JSON.stringify(credential);
    form.submit();
  } catch (error) {
    if (error.name !== "NotAllowedError") {
      console.error("Passkey error:", error);
    }
  }
}

// --- Registration (Create Passkey) ---

async function getChallengeAndSubmitRegistration(form) {
  if (!checkWebAuthnSupport()) return;

  const credentialFieldName = form.dataset.credentialFieldName;
  const challengeResponse = await postJSON(form.dataset.challengeUrl);
  const challengeJSON = await challengeResponse.json();
  const options = parseCreationOptionsFromJSON({ publicKey: challengeJSON });
  const credential = await create(options);

  form.elements[credentialFieldName].value = JSON.stringify(credential);
  form.submit();
}

// --- Reauthentication ---

async function getReauthenticationToken(form) {
  const tokenFieldName = form.dataset.reauthenticationTokenFieldName;

  const challengeResponse = await postJSON(
    form.dataset.reauthenticationChallengeUrl
  );
  const challengeJSON = await challengeResponse.json();
  const options = parseRequestOptionsFromJSON({ publicKey: challengeJSON });
  const credential = await get(options);

  const tokenResponse = await postJSON(
    form.dataset.reauthenticationTokenUrl,
    { "Content-Type": "application/json" },
    JSON.stringify({ passkey_credential: JSON.stringify(credential) })
  );

  const tokenData = await tokenResponse.json();
  form.elements[tokenFieldName].value = tokenData.reauthentication_token;
}

// --- Auto-bind ---

// Idempotent click binding: Turbo may execute this bundle repeatedly for the
// same DOM, so never stack a second listener on an element already bound (that
// duplicate listener is what fired the second challenge request).
function bindClick(el, handler) {
  if (!el || el.dataset.passkeyBound === "1") return;
  el.dataset.passkeyBound = "1";
  el.addEventListener("click", handler);
}

function bindPasskeyHandlers() {
  // Passkey login form
  const passkeyLoginForm = document.getElementById("passkey-form");
  if (passkeyLoginForm) {
    const form = passkeyLoginForm.querySelector("form");
    bindClick(document.getElementById("passkey-submit-btn"), (event) => {
      event.preventDefault();
      runCeremony(() => getChallengeAndSubmitCredential(form));
    });
  }

  // Passkey registration form
  const addPasskeyForm = document.getElementById("add-passkey-form");
  if (addPasskeyForm) {
    bindClick(
      addPasskeyForm.querySelector('input[type="submit"]'),
      (event) => {
        event.preventDefault();
        runCeremony(() => getChallengeAndSubmitRegistration(addPasskeyForm));
      }
    );

    bindClick(document.getElementById("unlock-button"), () =>
      runCeremony(async () => {
        await getReauthenticationToken(addPasskeyForm);
        addPasskeyForm
          .querySelector("#new-passkey-fields")
          .removeAttribute("disabled");
      })
    );
  }

  // Passkey delete forms
  document.querySelectorAll(".delete-passkey").forEach((form) => {
    bindClick(form.querySelector('input[type="submit"]'), (event) => {
      event.preventDefault();
      runCeremony(async () => {
        await getReauthenticationToken(form);
        form.submit();
      });
    });
  });
}

// Bind on first load and after every Turbo navigation. Both bindClick and the
// in-flight guard are idempotent, so binding more than once is harmless.
document.addEventListener("turbo:load", bindPasskeyHandlers);
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindPasskeyHandlers);
} else {
  bindPasskeyHandlers();
}
